import * as React from 'react'
import { withTheme } from 'styled-components'
import {
  getBlotScale,
  getBlotOpacity,
  getBlotShiftX,
  getBlotShiftY,
  getMaxRadius,
  merge,
  pixelRatio
} from './utils'
import { HAS_TOUCH } from './utils/hasTouch'
import { Store } from './utils/store'

const TAU = Math.PI * 2
const MOUSE_LEFT = 0

const STYLE = {
  borderRadius: 'inherit',
  height: '100%',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%'
}

const defaultProps = {
  background: false,
  className: 'waves',
  duration: 1000,
  opacity: 0.1,
  radius: 150,
  recenter: true,
  hasTouch: HAS_TOUCH,
  id: '',
}

class Waves extends React.PureComponent {
  constructor() {
    super(...arguments)

    this.state = {
      color: 'transparent',
      density: 1,
      height: 0,
      // eslint-disable-next-line babel/new-cap
      store: Store(this.tick.bind(this)),
      width: 0
    }

    this.touchEvents = this.touchEvents()
  }

  touchEvents() {
    if (this.props.hasTouch) {
      return {
        onTouchStart: this._onPress.bind(this),
        onTouchEnd: this._onRelease.bind(this),
        onTouchCancel: this._onRelease.bind(this)
      }
    } else {
      return {
        onMouseDown: this._onPress.bind(this),
        onMouseUp: this._onRelease.bind(this),
        onMouseLeave: this._onRelease.bind(this)
      }
    }
  }

  tick() {
    const { ctx, color, density, height, width, store } = this.state

    ctx.save()

    ctx.scale(density, density)

    ctx.clearRect(0, 0, width, height)

    ctx.fillStyle = color

    if (this.props.background) {
      ctx.globalAlpha = store.getTotalOpacity(this.props.opacity)
      ctx.fillRect(0, 0, width, height)
    }

    store.each(this.makeBlot, this)

    ctx.restore()
  }

  makeBlot(blot) {
    const { ctx, height, width } = this.state
    // eslint-disable-next-line prefer-const
    let { x, y, radius } = blot

    ctx.globalAlpha = getBlotOpacity(blot, this.props.opacity)
    ctx.beginPath()

    if (this.props.recenter) {
      const size = Math.max(height, width)

      x += getBlotShiftX(blot, size, width)
      y += getBlotShiftY(blot, size, height)
    }

    ctx.arc(x, y, radius * getBlotScale(blot), 0, TAU)

    ctx.closePath()
    ctx.fill()
  }

  componentWillUnmount() {
    this.state.store.stop()
  }

  pushBlot(timeStamp, clientX, clientY) {
    let el = this.canvas

    // 0.13 support
    if (el.getDOMNode && 'function' === typeof el.getDOMNode) {
      el = el.getDOMNode()
    }

    const { top, bottom, left, right } = el.getBoundingClientRect()
    const color = this.props.color
      ? this.props.theme.color[this.props.color] || this.props.theme.color.white
      : 'white'

    const ctx = this.state.ctx || el.getContext('2d')
    const density = pixelRatio(ctx)
    const height = bottom - top
    const width = right - left
    const radius = getMaxRadius(height, width, this.props.radius)

    this.setState({ color, ctx, density, height, width }, () => {
      this.state.store.add({
        duration: this.props.duration,
        mouseDown: timeStamp,
        mouseUp: 0,
        radius,
        x: clientX - left,
        y: clientY - top
      })
    })
  }

  setCanvas(el) {
    this.canvas = el
  }

  render() {
    const { density, height, width } = this.state
    const { style } = this.props

    const props = merge(
      {
        id: this.props.id,
        className: this.props.className,
        ref: this.setCanvas.bind(this),
        height: height * density,
        width: width * density,
        onDragOver: this._onRelease,
        style: merge(STYLE, style)
      },
      this.touchEvents
    )

    return React.createElement('canvas', props)
  }

  _onPress(event) {
    const { button, ctrlKey, clientX, clientY, changedTouches } = event
    const timeStamp = Date.now()

    if (changedTouches) {
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < changedTouches.length; i++) {
        const { clientX, clientY } = changedTouches[i]
        this.pushBlot(timeStamp, clientX, clientY)
      }
    } else if (button === MOUSE_LEFT && !ctrlKey) {
      this.pushBlot(timeStamp, clientX, clientY)
    }
  }

  _onRelease() {
    this && this.state.store.release(Date.now())
  }
}

export default withTheme(Waves)

Waves.defaultProps = defaultProps
